import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
} from '@angular/core';
import { ChartComponent, ChartHeader, ChartType, GaugeInputs } from '@charts';
import { TranslocoDirective } from '@jsverse/transloco';
import { ActionCardIconComponent, IconComponent } from '@ui';
import { GetLossesAverageResponse } from '@data';
import {
  TranslocoDatePipe,
  TranslocoDecimalPipe,
} from '@jsverse/transloco-locale';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ChartComponent,
    TranslocoDirective,
    TranslocoDatePipe,
    IconComponent,
    ActionCardIconComponent,
    ChartHeader,
    TranslocoDecimalPipe,
  ],
  selector: 'ui-losses-average-chart',
  template: `
    <ng-container *transloco="let t">
      <div
        class="flex flex-col h-full w-full bg-white p-6 rounded-lg border border-gray-200"
      >
        <div class="flex items-center mb-[24px]">
          <ui-action-card-icon icon class="{{ iconClass() }}" [icon]="icon()" />
          <chart-header
            [title]="t('CHARTS.LOSSES.TITLE')"
            [subtitle]="
              t('CHARTS.LOSSES.SUBTITLE') +
              ' ' +
              (todayDate() | translocoDate: { dateStyle: 'short' })
            "
          />
        </div>
        <div class="mb-3">
          <chart
            class="mx-4"
            [type]="chartType()"
            [typeInputs]="gaugeInputs()"
          />
        </div>
        <div class="flex justify-around text-center">
          <div>
            <p class="text-sm text-neutral-7 text-center mb-1">
              {{ t('CHARTS.LOSSES.AVERAGE.LEGEND_WEEK') }}
            </p>
            <div class="text-2xl font-medium leading-[32px]">
              {{ data().averageWeeklyLossPercentage | translocoDecimal }}%
              @if (data().isHigherThanWeeklyAverage) {
                <ui-icon class="text-green text-xl" icon="arrow-up" />
              } @else {
                <ui-icon class="text-red-400 text-xl" icon="arrow-down" />
              }
            </div>
          </div>
          <div>
            <p class="text-sm text-neutral-7 text-center mb-1">
              {{ t('CHARTS.LOSSES.AVERAGE.LEGEND_MONTH') }}
            </p>
            <div class="text-2xl font-medium leading-[32px]">
              {{ data().averageMonthlyLossPercentage | translocoDecimal }}%
              @if (data().isHigherThanMonthlyAverage) {
                <ui-icon class="text-green text-xl" icon="arrow-up" />
              } @else {
                <ui-icon class="text-red-400 text-xl" icon="arrow-down" />
              }
            </div>
          </div>
        </div>
      </div>
    </ng-container>
  `,
})
export class LossesAverageChartComponent {
  data = input.required<GetLossesAverageResponse>();
  icon = signal<string>('line-chart');
  iconClass = signal(
    'text-yellow-500 bg-yellow-50 shadow-custom-orange me-[16px]',
  );
  todayDate = input.required<string>();
  gaugeInputs = computed<GaugeInputs>((): GaugeInputs => {
    return {
      value: this.data().currentLossPercentage,
      min: 0,
      max: 100,
      units: '%',
      showPercentage: true,
      progressColor: '#D4B106',
      showValue: false,
      showBadge: false,
      showMaxMin: false,
      showVariations: false,
    };
  });

  chartType = signal<ChartType>('gauge');
}
